import { makeAutoObservable } from 'mobx';

import { APIUserDTO } from "../DTOs/APIUserDTO";
import { StockistCoverAlertFrequency, StockistDTO } from "../DTOs/StockistDTO";
import { AddressDTO } from "../DTOs/Address/AddressDTO";
import { UserIpResponse } from "../DTOs/UserIpResponse";
import { ValidationResponse } from "../DTOs/ValidationResponse";
import { ajaxController } from "./AjaxController";

//#region POST Request models
type UserCreateRequest = {
  stockistName: string
  email: string
  password: string
  billingAddress: AddressDTO
  contactFirstName: string
  contactSurname: string
  contactTel: string
  VATNo: string
  eori: string
  stockistType: string
  coverAlertsFrequency: StockistCoverAlertFrequency
}

type UserUpdateRequest = {
  stockistName: string
  email: string
  contactFirstName: string
  contactSurname: string
  contactTel: string
  VATNo: string
  eori: string
  coverAlertsFrequency: StockistCoverAlertFrequency
}

export type ValidateInitialUserInfoRequest = {
  stockistName: string
  stockistType: string
  email: string
  password: string
  contactFirstName: string
  contactSurname: string
  VATNo: string
}

type LogInRequest = {
  email: string
  password: string
}

type PasswordChangeRequest = {
  stockistId: number
  password: string
  confirmPassword: string
}

type SsoLoginRequest = {
  email: string
  token: string
}

//#endregion

export class UserAjaxController {
  private _ajaxController = ajaxController;
  private _stockistEndpoint = "stockist"
  private _authenticationEndpoint = "authentication"

  validateUser(data: UserCreateRequest) {
    return this._ajaxController.post<UserCreateRequest, ValidationResponse>(`${this._stockistEndpoint}/create/validate`, data, ValidationResponse)
  }

  validateInitialUserInfo(data: ValidateInitialUserInfoRequest) {
    return this._ajaxController.post<ValidateInitialUserInfoRequest, ValidationResponse>(`${this._stockistEndpoint}/create/validate/initial`, data, ValidationResponse)
  }

  createUser(data: UserCreateRequest) {
    return this._ajaxController.post<UserCreateRequest, APIUserDTO>(`${this._stockistEndpoint}/create`, data, APIUserDTO)
  }

  updateUser(data: UserUpdateRequest) {
    return this._ajaxController.post<UserUpdateRequest, StockistDTO>(`${this._stockistEndpoint}/updateDetails`, data, StockistDTO)
  }

  login(email: string, password: string) {
    const data = {
      email: email,
      password: password
    }
    return this._ajaxController.post<LogInRequest, APIUserDTO>(`${this._authenticationEndpoint}/${this._stockistEndpoint}`, data, APIUserDTO)
  }

  changePassword(data: PasswordChangeRequest) {
    return this._ajaxController.post<PasswordChangeRequest, void>(`${this._stockistEndpoint}/changePassword`, data, APIUserDTO)
  }

  initializeResetPassword(email: string) {
    return this._ajaxController.postNoCast<{ email: string }, void>(`${this._stockistEndpoint}/resetPassword/initialize`, { email })
  }

  verifyResetPasswordToken(token: string) {
    return this._ajaxController.post<{ token: string }, ValidationResponse>(`${this._stockistEndpoint}/resetPassword/verifyToken`, { token }, ValidationResponse)
  }

  resetPassword(token: string, password: string, confirmPassword: string) {
    return this._ajaxController.post<{ token: string, password: string, confirmPassword: string }, ValidationResponse>(`${this._stockistEndpoint}/resetPassword`, { token, password, confirmPassword }, ValidationResponse)
  }

  ssoLogin(email: string, token: string) {
    const data = { email, token } as SsoLoginRequest

    return this._ajaxController.post<SsoLoginRequest, APIUserDTO>(`${this._authenticationEndpoint}/${this._stockistEndpoint}/sso`, data, APIUserDTO);
  }

  logout() {
    return this._ajaxController.post(`${this._authenticationEndpoint}/${this._stockistEndpoint}/logOff`, {}, APIUserDTO)
  }

  getApiUser(userId: number) {
    return this._ajaxController.get<APIUserDTO>(`${this._stockistEndpoint}/apiUser/${userId}`, {}, APIUserDTO)
  }

  getStockist(userId: number) {
    return this._ajaxController.get<StockistDTO>(`${this._stockistEndpoint}/${userId}`, {}, StockistDTO)
  }

  // this is extremely interesting as axios cannot handle this request, thus using fetch
  getUserIp = async (): Promise<string> => {
    return (await fetch('https://api.ipify.org/?format=json').then(response => response.json() as Promise<UserIpResponse>)).ip;
  }

  constructor() {
    makeAutoObservable(this);
  }
}

export const userAjaxController = new UserAjaxController();